import { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface PhoneFrameProps {
  children: ReactNode;
  className?: string;
  metal?: boolean;
  compact?: boolean;
  hideNotch?: boolean;
}

/**
 * Pure-CSS iPhone bezel wrapper. Children render inside a 9:16 screen.
 * Fully responsive — sized via max-width, scales fluidly inside any grid cell.
 */
export function PhoneFrame({ children, className, metal = false, compact = false, hideNotch = false }: PhoneFrameProps) {
  return (
    <div
      className={cn(
        "relative mx-auto w-full max-w-[260px]",
        compact ? "rounded-[1.4rem] p-[6px]" : "rounded-[2.5rem] p-[10px]",
        metal
          ? "phone-frame-metal"
          : "bg-gradient-to-b from-neutral-800 via-neutral-900 to-black shadow-[0_25px_50px_-12px_rgba(0,0,0,0.6),0_0_0_2px_rgba(255,255,255,0.05)_inset]",
        className
      )}
    >
      {/* Side buttons */}
      <span aria-hidden className={cn("absolute left-[-2px] w-[3px] rounded-l", compact ? "top-[46px] h-4" : "top-[88px] h-8", metal ? "phone-frame-metal-button" : "bg-neutral-700")} />
      <span aria-hidden className={cn("absolute left-[-2px] w-[3px] rounded-l", compact ? "top-[68px] h-6" : "top-[130px] h-12", metal ? "phone-frame-metal-button" : "bg-neutral-700")} />
      <span aria-hidden className={cn("absolute left-[-2px] w-[3px] rounded-l", compact ? "top-[98px] h-6" : "top-[188px] h-12", metal ? "phone-frame-metal-button" : "bg-neutral-700")} />
      <span aria-hidden className={cn("absolute right-[-2px] w-[3px] rounded-r", compact ? "top-[74px] h-8" : "top-[140px] h-16", metal ? "phone-frame-metal-button" : "bg-neutral-700")} />

      {/* Inner screen */}
      <div className={cn("relative aspect-[9/16] w-full overflow-hidden", compact ? "rounded-[1.1rem]" : "rounded-[2rem]", metal ? "phone-frame-metal-screen" : "bg-black")}>
        {/* Notch */}
        {!hideNotch && <div
          aria-hidden
          className={cn("pointer-events-none absolute left-1/2 z-10 -translate-x-1/2 rounded-full", compact ? "top-1 h-2.5 w-10" : "top-2 h-5 w-20", metal ? "phone-frame-notch" : "bg-black")}
        />}
        {children}
      </div>
    </div>
  );
}